import { ReactCore, ReactExtensions, ReactExtensionContainer, Gabarit, ReactLoadedExtensions } from './utils/types';
import { ReactTranslation, SupportedLanguages } from './utils/translation';
import { MouseEvent } from 'react';
import { useDisplay, useDisplayList } from './displayer/useDisplay';
import Router, { ReactRouter } from './utils/router';
import { JInjectStore } from './redux/store';
import { TinyMCECommonOptions } from 'jamespot-user-api';
import { ReactCommonRegistry } from './registry/ReactCommonRegistry';
import { ReactCoreRegistry } from './registry/ReactCoreRegistry';
import { ReactSocket } from './utils/types.socket';
import { GlobalRegistry } from './registry/GlobalRegistry';
import { unmountComponentAtNode } from 'react-dom';

class App implements ReactCore {
    extensions: ReactExtensions = {};
    loadedExtensions: ReactLoadedExtensions = {};
    store: JInjectStore;
    actions: object;
    registry: GlobalRegistry;
    useDisplay: typeof useDisplay;
    useDisplayList: typeof useDisplayList;
    locale: SupportedLanguages;
    translation: ReactTranslation;
    theme: any;
    socket: ReactSocket;
    router: ReactRouter = Router;
    tinymceCommonOptions?: TinyMCECommonOptions;

    constructor(
        store: JInjectStore,
        ActionCreators: object,
        registry: { commonComponentsRegistry: ReactCommonRegistry; coreComponentsRegistry: ReactCoreRegistry },
        locale: SupportedLanguages,
        translation: ReactTranslation,
        socket: ReactSocket,
    ) {
        this.store = store;
        this.actions = ActionCreators;
        this.registry = new GlobalRegistry(registry.commonComponentsRegistry, registry.coreComponentsRegistry);
        this.useDisplay = useDisplay;
        this.useDisplayList = useDisplayList;
        this.locale = locale;
        this.translation = translation;
        this.socket = socket;
    }

    public require = (extensionName: string, args?: any) => {
        const module = this.loadedExtensions[extensionName]?.default;
        if (module?.initExtension) {
            module.initExtension(args);
        } else if (this.extensions[extensionName]) {
            this.extensions[extensionName]().then((module: ReactExtensionContainer) => {
                this.loadedExtensions[extensionName] = module;
                const m = module.default;
                if (m.initExtension) {
                    m.initExtension(args);
                }
            });
        }
    };

    public unmountExtension = (extensionName: string, args?: any) => {
        const extension = this.loadedExtensions[extensionName];
        if (extension) {
            extension.default.unmountExtension({ params: args });
        }
    };

    public extensionAdd(extensionName: string, load: () => void) {
        this.extensions[extensionName] = load;
    }

    public routeAdd(
        route: string,
        extensionName: string,
        idDiv: string,
        gabarit: Gabarit = 'app-1-cols',
        gabaritOptions?: object,
    ) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        if (!J.ng) {
            return;
        }
        J.ng.addState(extensionName, {
            abstract: true,
            templateUrl: '/?action=tpl&tpl=content&_=' + J.revision,
            controller: [
                '$jTheme',
                function ($jTheme: any) {
                    $jTheme.setGabarit(gabarit, gabaritOptions);
                },
            ],
        });
        J.ng.addState(extensionName + '.app', {
            template: '',
            url: '/r/' + route,
            reloadOnSearch: false,
            controller: [
                '$scope',
                '$rootScope',
                '$timeout',
                function ($scope: any, $rootScope: any, $timeout: any) {
                    $scope.appId = idDiv;
                    $timeout(function () {
                        self.require(extensionName);
                    });

                    const stateChangeStart = $rootScope.$on('$stateChangeStart', function () {
                        const el = document.getElementById(idDiv);
                        if (el) {
                            unmountComponentAtNode(el);
                        }
                    });
                    $scope.$on('$destroy', function () {
                        stateChangeStart();
                    });
                },
            ],
        });
    }

    public transitionTo(
        stateName: string,
        args: {},
        options: {
            reload: boolean;
            notify?: boolean;
            callback?: () => void;
            event?: MouseEvent<HTMLElement>;
        } = { reload: false, notify: false },
    ) {
        const { reload, notify, callback, event } = options;
        J.ng.transitionTo(stateName + '.app', args, {
            reload,
            notify: notify || false,
            ...(callback && { callback: callback }),
            ...(event && (event.ctrlKey || event.metaKey) && { event: event }),
        });
    }

    public changeWindowLocation(targetUrl: string, event: MouseEvent<HTMLElement>, enableWindowOpen = false) {
        event.preventDefault();
        event.stopPropagation();
        if (enableWindowOpen || event.ctrlKey || event.metaKey) {
            window.open(targetUrl, '_blank')?.focus();
        } else {
            window.location.href = targetUrl;
        }
    }
}

export default App;
